import {
    Component,
    OnInit,
    OnDestroy,
    Output,
    EventEmitter
} from '@angular/core'
import { CommonModule } from '@angular/common'
import { FormsModule } from '@angular/forms'
import { Router } from '@angular/router'

import { MatSelectModule } from '@angular/material/select'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatIconModule } from '@angular/material/icon'

import { TranslateModule } from '@ngx-translate/core'
import { LucideAngularModule } from 'lucide-angular'
import { Subject, Subscription } from 'rxjs'

import { LOCAL_STORAG_KEYS } from '@@constants/local-storage-keys'
import { CUSTOM_EVENTS } from '@@constants/custom-events'

import cryptoEncryptData from '@@utils/cryptoEncryptData'

import { AppService } from '@@services/app.service'
import { AuthService } from '@@services/auth.service'
import { BranchManagementService } from '@@services/branch-management.service'
import { SettingsService } from '@@services/settings.service'
import { GeneralService } from '@@services/general.service'

import { PopupPanelComponent } from '@@shared/componenets/popup-panel/popup-panel.component'

@Component({
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        MatSelectModule,
        MatFormFieldModule,
        MatIconModule,
        TranslateModule,
        LucideAngularModule,
        PopupPanelComponent
    ],
    selector: 'business-switcher',
    templateUrl: './business-switcher.component.html',
    styleUrls: ['./business-switcher.component.scss']
})
export class BusinessSwitcherComponent implements OnInit, OnDestroy {
    static newSelectedBranch = new Subject<number>()
    static _newSelectedBranch =
        BusinessSwitcherComponent.newSelectedBranch.asObservable()

    @Output() branchChanged = new EventEmitter<void>()
    @Output() panelToggled = new EventEmitter<boolean>()

    companyName: string | null = localStorage.getItem(
        LOCAL_STORAG_KEYS.COMPANY_NAME
    )
    tenantImageUrl: string | null = null
    branchName: string = ''
    branches: Array<any> = []
    selectedBranch: number | null = null
    tenants: Array<any> = []
    currentTenantId: string | null = null
    isPanelOpen: boolean = false

    private subscriptions: Subscription[] = []

    constructor(
        private appService: AppService,
        private authService: AuthService,
        private settingsService: SettingsService,
        private router: Router,
        private branchManagementService: BranchManagementService
    ) {}

    ngOnInit() {

        this.currentTenantId = localStorage.getItem(LOCAL_STORAG_KEYS.TENANT_ID)
        this.getUserAssignedBranches()
        this.loadTenants()

        this.subscriptions.push(
            BusinessSwitcherComponent._newSelectedBranch.subscribe((branchId: number) => {
                this.selectedBranch = branchId
                const branch = this.branches?.find((b: any) => b.id == branchId)
                if (branch) {
                    this.branchName = branch.name
                }
            })
        )

        this.subscriptions.push(
            GeneralService.tenantChangeEvent.subscribe(() => {
                this.currentTenantId = localStorage.getItem(
                    LOCAL_STORAG_KEYS.TENANT_ID
                )
                this.companyName = localStorage.getItem(
                    LOCAL_STORAG_KEYS.COMPANY_NAME
                )
                this.tenantImageUrl = localStorage.getItem(
                    LOCAL_STORAG_KEYS.TENANT_IMAGE_URL
                )
                this.getUserAssignedBranches()
            })
        )

        this.subscriptions.push(
            GeneralService.customEvent.subscribe((event: string) => {
                if (event === CUSTOM_EVENTS.IN2_SUSCRIPTION_WARNING_MESSAGE_CHNAGED) {
                    this.tenantImageUrl = localStorage.getItem(
                        LOCAL_STORAG_KEYS.TENANT_IMAGE_URL
                    )
                    this.companyName = localStorage.getItem(
                        LOCAL_STORAG_KEYS.COMPANY_NAME
                    )
                }
            })
        )
    }

    ngOnDestroy() {
        this.subscriptions.forEach((s) => s.unsubscribe())
    }

    togglePanel(event: MouseEvent) {
        event.stopPropagation()
        this.isPanelOpen = !this.isPanelOpen
        this.panelToggled.emit(this.isPanelOpen)
    }

    closePanel() {
        this.isPanelOpen = false
        this.panelToggled.emit(false)
    }

    onLogoError() {
        this.tenantImageUrl = null
    }

    getUserAssignedBranches() {
        this.branches = []
        this.appService.getUserAssignedBranches().subscribe((response) => {
            setTimeout(() => {
                if (response.branches) this.branches = response.branches

                localStorage.setItem(
                    LOCAL_STORAG_KEYS.TENANT_BRANCHES_FULL,
                    JSON.stringify(this.branches)
                )
                if (
                    localStorage.getItem(LOCAL_STORAG_KEYS.TENANT_ID) &&
                    (!this.branches || this.branches.length == 0)
                )
                    this.router
                        .navigateByUrl('/portal/company/selection')
                        .catch((err) => {
                            console.error(err)
                        })

                const idSelectedBranch =
                    this.branchManagementService.getCurrentBranchId()
                if (idSelectedBranch !== null) {
                    this.selectedBranch = idSelectedBranch
                }

                let selectedBranch = this.branches?.find(
                    (b) => b.id == this.selectedBranch
                )

                // Fallback to first branch if stored ID doesn't match (e.g. after tenant switch)
                if (!selectedBranch && this.branches?.length > 0) {
                    selectedBranch = this.branches[0]
                    this.selectedBranch = selectedBranch.id
                    this.branchManagementService.setCurrentBranchId(
                        selectedBranch.id
                    )
                }

                if (selectedBranch) {
                    this.branchName = selectedBranch.name
                    this.fillLocalStorageBranchConfigs(selectedBranch)
                }
            }, 100)
        })
    }

    changeSelectedBranch(event: any) {
        if (!event || event == '') {
            return
        }

        let branchTenant: { [key: string]: string } = {}
        this.branches?.forEach((branch) => {
            branchTenant[branch.id] = branch.idTenant
        })

        let selectedBranch = this.branches?.filter((b) => b.id == event)[0]

        let idTenant = branchTenant[event]
        localStorage.setItem(LOCAL_STORAG_KEYS.TENANT_ID, idTenant)
        this.branchManagementService.setCurrentBranchId(+event)

        if (selectedBranch) {
            this.branchName = selectedBranch.name
            this.fillLocalStorageBranchConfigs(selectedBranch)
        }

        BusinessSwitcherComponent.newSelectedBranch.next(event)
        this.branchChanged.emit()
    }

    loadTenants() {
        this.appService.getUserTenants().subscribe((response: any) => {
            if (response && response.tenants) {
                this.tenants = response.tenants
            }
        })
    }

    switchTenant(tenant: any) {
        if (tenant.id == this.currentTenantId) return

        localStorage.setItem(LOCAL_STORAG_KEYS.TENANT_ID, '' + tenant.id)
        localStorage.setItem(
            LOCAL_STORAG_KEYS.ROLE_ID,
            '' + cryptoEncryptData(tenant.roleId)
        )
        localStorage.setItem(LOCAL_STORAG_KEYS.COMPANY_NAME, tenant.name ?? '')
        localStorage.removeItem(LOCAL_STORAG_KEYS.TENANT_IMAGE_URL)

        this.closePanel()

        this.appService.getUserAssignedBranches().subscribe({
            next: (response) => {
                if (response?.branches?.length > 0) {
                    this.branchManagementService.setCurrentBranchId(
                        response.branches[0].id
                    )
                } else {
                    this.branchManagementService.clearCurrentBranchId()
                }
                localStorage.setItem(LOCAL_STORAG_KEYS.COMPANY_SELECTION_ROUTE_ACTIVATED, Date.now().toString())
                window.location.reload()
            },
            error: () => {
                this.branchManagementService.clearCurrentBranchId()
                localStorage.setItem(LOCAL_STORAG_KEYS.COMPANY_SELECTION_ROUTE_ACTIVATED, Date.now().toString())
                window.location.reload()
            }
        })
    }

    private fillLocalStorageBranchConfigs(selectedBranch: any) {
        setTimeout(() => {
            this.performFillLocalStorageBranchConfigs(selectedBranch)
        }, 1000)

        setTimeout(() => {
            this.performFillLocalStorageBranchConfigs(selectedBranch)
        }, 3000)
    }

    private performFillLocalStorageBranchConfigs(selectedBranch: any) {
        if (selectedBranch.localServerAddress) {
            localStorage.setItem(
                LOCAL_STORAG_KEYS.LOCAL_SERVER_ADDRESS,
                selectedBranch.localServerAddress
            )
        } else {
            localStorage.removeItem(LOCAL_STORAG_KEYS.LOCAL_SERVER_ADDRESS)
        }

        if (selectedBranch.localAccessWindow) {
            localStorage.setItem(
                LOCAL_STORAG_KEYS.LOCAL_ACCESS_WINDOW,
                selectedBranch.localAccessWindow
            )
        } else {
            localStorage.removeItem(LOCAL_STORAG_KEYS.LOCAL_ACCESS_WINDOW)
        }
    }
}
